import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  limit,
  getDoc,
} from "firebase/firestore";
import {
  AdminWordService,
  AdminWordServiceOptions,
  WordOperationResult,
  ChangeAction,
} from "../AdminWordService";
import { WordExportType, WordTypeWithImages } from "../../features/words/types";
import {
  wordsCollection,
  getChangesRef,
  imagesIndexCollection,
} from "./firestore";
import {
  mapFromDocumentData,
  mapFromDocumentDataToExportWord,
} from "../../features/words/mapper";
import { where } from "firebase/firestore";
import { getNextString } from "../../features/words/utils";
import { WordImage } from "../WordService";

export class FirebaseAdminWordService extends AdminWordService {
  // Private method for change tracking to avoid context issues
  private async trackChange(change: ChangeAction): Promise<void> {
    console.log("Logging change action:", change);
    try {
      const collection = await getChangesRef();
      if (collection) {
        await setDoc(doc(collection, change.word.word), {
          ...change.word,
          action: change.action,
        });
        console.log(
          `'${change.word.word}' added to versioning for '${change.action}'`
        );
      }
    } catch (error) {
      console.error("Error adding change:", error);
      // Don't throw here as this is logging/tracking functionality
    }
  }

  async addWord(word: WordTypeWithImages): Promise<WordOperationResult> {
    try {
      console.log("Adding word:", word);
      await setDoc(doc(wordsCollection, word.word), word);
      await this.trackChange({ word, action: "add" });

      return {
        success: true,
        data: word,
      };
    } catch (error) {
      console.error("Error adding word:", error);
      return {
        success: false,
        error: `Error adding ${word.word}: ${error}`,
      };
    }
  }

  async getWord(word: string): Promise<WordOperationResult> {
    return getDoc(doc(wordsCollection, word)).then((doc) => {
      if (doc.exists()) {
        return {
          success: true,
          data: mapFromDocumentData(doc.data()),
        };
      }
      return {
        success: false,
        error: `Word not found: ${word}`,
      };
    });
  }

  async updateWord(word: WordTypeWithImages): Promise<WordOperationResult> {
    try {
      await updateDoc(doc(wordsCollection, word.word), { ...word });
      await this.trackChange({ word, action: "edit" });

      return {
        success: true,
        data: word,
      };
    } catch (error) {
      console.error("Error updating word:", error);
      return {
        success: false,
        error: `Error updating ${word.word}: ${error}`,
      };
    }
  }

  async deleteWord(word: string): Promise<boolean> {
    try {
      await deleteDoc(doc(wordsCollection, word));
      await this.trackChange({ word: { word }, action: "delete" });

      return true;
    } catch (error) {
      console.error("Error deleting word:", error);
      return false;
    }
  }

  async renameWord(
    oldWord: string,
    newWord: WordTypeWithImages
  ): Promise<WordOperationResult> {
    try {
      // First delete the old word
      const deleted = await this.deleteWord(oldWord);
      if (!deleted) {
        return {
          success: false,
          error: `Error deleting old word: ${oldWord}`,
        };
      }

      // Then add the new word
      return await this.addWord(newWord);
    } catch (error) {
      console.error("Error renaming word:", error);
      return {
        success: false,
        error: `Error renaming ${oldWord} to ${newWord.word}: ${error}`,
      };
    }
  }

  async exportWords(
    options?: AdminWordServiceOptions
  ): Promise<WordExportType[]> {
    try {
      let q;
      if (options?.fullExport) {
        q = query(wordsCollection, orderBy("index"));
      } else {
        q = query(
          wordsCollection,
          orderBy("index"),
          limit(options?.limit || 20)
        );
      }

      const querySnapshot = await getDocs(q);
      const words: WordExportType[] = [];

      querySnapshot.forEach((doc) => {
        if (doc.exists()) {
          words.push(mapFromDocumentDataToExportWord(doc.data()));
        }
      });

      return words;
    } catch (error) {
      console.error("Error exporting words:", error);
      throw error;
    }
  }

  async addChange(change: ChangeAction): Promise<void> {
    return this.trackChange(change);
  }

  // Search for images by keyword
  async searchImages(
    searchWord: string,
    limitCount: number = 10
  ): Promise<WordImage[]> {
    try {
      const startsWith = searchWord;
      const endSearch = getNextString(startsWith);

      const q = query(
        imagesIndexCollection,
        where("name", ">=", startsWith),
        where("name", "<", endSearch),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const imagesLink: WordImage[] = [];

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        docData.urls.forEach((url: string) =>
          imagesLink.push({ id: docData.name, keyword: docData.name, url: url })
        );
      });

      return imagesLink;
    } catch (error) {
      console.error("Error searching images:", error);
      return [];
    }
  }
}
